import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { Preferences } from '@capacitor/preferences';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Moon,
  UserCog,
  Fingerprint,
  FileText,
  ShieldCheck,
  Cookie,
  ChevronRight,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Shield,
} from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { trackEvent } from '@/lib/analytics';
import CustomizeModal from '@/components/CookieConsent/CustomizeModal';
import sabotagelogo from '@/assets/sabotage-logo.png';

const LEGAL_LINKS = {
  terms: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=0eef0aaa-538d-4434-9471-2d372eb58c22',
  privacy: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=74b4f847-6b13-45b6-9a95-fa2cd405c4a6',
  cookies: 'https://app.termly.io/policy-viewer/policy.html?policyUUID=b68d815d-0463-455f-8bb1-1aef0ad6efd2',
};

const openLink = async (url: string) => {
  await Browser.open({ url, presentationStyle: 'popover' });
};

const AppSettings = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();
  const [cookiePrefsOpen, setCookiePrefsOpen] = useState(false);

  // Biometric state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState('Face ID / Touch ID');
  const [biometricLoading, setBiometricLoading] = useState(true);

  useEffect(() => {
    const checkBiometrics = async () => {
      try {
        const result = await BiometricAuth.checkBiometry();
        setBiometricAvailable(result.isAvailable);
        if (result.biometryType === BiometryType.faceId) {
          setBiometricLabel('Face ID');
        } else if (result.biometryType === BiometryType.touchId) {
          setBiometricLabel('Touch ID');
        } else if (result.biometryType === BiometryType.fingerprintAuthentication) {
          setBiometricLabel('Fingerprint');
        } else {
          setBiometricLabel('Biometrics');
        }

        // Check if already enabled
        const flag = localStorage.getItem('sf-biometric-enabled');
        if (flag === 'true' && result.isAvailable) {
          setBiometricEnabled(true);
        }
      } catch {
        setBiometricAvailable(false);
      } finally {
        setBiometricLoading(false);
      }
    };
    checkBiometrics();
  }, []);

  // Biometric login disabled while we migrate to secure storage (SAB-232).
  // The previous flow stored the user's password in plaintext via Capacitor
  // Preferences (unencrypted UserDefaults / SharedPreferences).
  const handleBiometricToggle = async (_enabled: boolean) => {
    toast({
      title: 'Biometric sign-in is being upgraded',
      description: 'We are moving credentials to secure storage. This feature will return shortly.',
    });
    // Belt-and-suspenders cleanup of any legacy plaintext credentials.
    try {
      await Preferences.remove({ key: 'sf-bio-email' });
      await Preferences.remove({ key: 'sf-bio-password' });
    } catch { /* not on Capacitor */ }
    localStorage.removeItem('sf-biometric-enabled');
    setBiometricEnabled(false);
  };

  // Biometric password prompt state
  const [biometricPasswordOpen, setBiometricPasswordOpen] = useState(false);
  const [bioPassword, setBioPassword] = useState('');
  const [showBioPassword, setShowBioPassword] = useState(false);
  const [bioPasswordError, setBioPasswordError] = useState('');
  const [bioPasswordSubmitting, setBioPasswordSubmitting] = useState(false);

  const resetBioPasswordForm = () => {
    setBioPassword('');
    setShowBioPassword(false);
    setBioPasswordError('');
  };

  // Disabled — used to write the password to unencrypted Preferences.
  const handleBioPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBiometricPasswordOpen(false);
    resetBioPasswordForm();
  };

  // Change Email state
  const [emailOpen, setEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  const resetEmailForm = () => {
    setNewEmail('');
    setEmailPassword('');
    setShowEmailPassword(false);
    setEmailError('');
    setEmailSuccess(false);
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError('');

    const trimmedEmail = newEmail.trim().toLowerCase();
    if (trimmedEmail === user?.email) {
      setEmailError('New email must be different from your current email.');
      return;
    }

    setEmailSubmitting(true);
    try {
      // Re-authenticate with password
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: emailPassword,
      });
      if (authError) {
        setEmailError('Password is incorrect.');
        return;
      }

      // Request email change
      const { error: updateError } = await supabase.auth.updateUser({ email: trimmedEmail });
      if (updateError) {
        setEmailError(updateError.message);
        return;
      }

      trackEvent('email_changed');
      setEmailSuccess(true);
      setTimeout(() => {
        setEmailOpen(false);
        resetEmailForm();
      }, 3000);
    } catch {
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  // Change Password state
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setPasswordError('');
    setPasswordSuccess(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      // Re-authenticate with current password
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: currentPassword,
      });
      if (authError) {
        setPasswordError('Current password is incorrect.');
        return;
      }

      // Update to new password
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        setPasswordError(updateError.message);
        return;
      }

      trackEvent('password_changed');
      setPasswordSuccess(true);
      setTimeout(() => {
        setPasswordOpen(false);
        resetPasswordForm();
      }, 1500);
    } catch {
      setPasswordError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Account state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const resetDeleteForm = () => {
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }

    setDeleting(true);
    setDeleteError('');
    try {
      // Insert deletion request
      const { error: insertError } = await supabase
        .from('deletion_requests')
        .insert({
          user_id: user!.id,
          email: user!.email || '',
        });

      if (insertError) {
        setDeleteError(insertError.message);
        return;
      }

      trackEvent('account_deleted');

      // Sign out and redirect
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch {
      setDeleteError('Something went wrong. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Suspend Account state
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendDuration, setSuspendDuration] = useState<'1week' | '2weeks' | '1month' | 'custom' | null>(null);
  const [customDate, setCustomDate] = useState('');
  const [suspendError, setSuspendError] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [suspendSuccess, setSuspendSuccess] = useState(false);

  const resetSuspendForm = () => {
    setSuspendDuration(null);
    setCustomDate('');
    setSuspendError('');
    setSuspendSuccess(false);
  };

  const getSuspendUntilDate = (): Date | null => {
    const now = new Date();
    switch (suspendDuration) {
      case '1week':
        return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      case '2weeks':
        return new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
      case '1month':
        return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      case 'custom':
        if (!customDate) return null;
        return new Date(customDate);
      default:
        return null;
    }
  };

  const getMinCustomDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  };

  const handleSuspendAccount = async () => {
    setSuspendError('');
    const until = getSuspendUntilDate();
    if (!until) {
      setSuspendError('Please select a suspension duration.');
      return;
    }

    if (suspendDuration === 'custom') {
      const minDate = new Date();
      minDate.setDate(minDate.getDate() + 30);
      if (until < minDate) {
        setSuspendError('Custom suspension must be at least 30 days.');
        return;
      }
    }

    setSuspending(true);
    try {
      const { error } = await supabase
        .from('account_suspensions')
        .insert({
          user_id: user!.id,
          suspended_until: until.toISOString(),
        });

      if (error) {
        setSuspendError(error.message);
        return;
      }

      const durationDays = Math.round((until.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      trackEvent('account_suspended', { duration_days: durationDays });
      setSuspendSuccess(true);
      setTimeout(async () => {
        await supabase.auth.signOut();
        window.location.href = '/auth';
      }, 2000);
    } catch {
      setSuspendError('Something went wrong. Please try again.');
    } finally {
      setSuspending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-3 flex items-center h-14 max-w-5xl gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-7 w-auto" />
          <span className="font-display font-bold text-foreground">App Settings</span>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-6 max-w-5xl space-y-4 pb-24 md:pb-6">

        {/* Appearance */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Moon className="w-4 h-4 text-primary" />
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Dark Mode</p>
                <p className="text-xs text-muted-foreground">{theme === 'dark' ? 'Dark theme active' : 'Light theme active'}</p>
              </div>
              <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} />
            </div>
          </CardContent>
        </Card>

        {/* Account */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCog className="w-4 h-4 text-primary" />
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <button
              onClick={() => { resetEmailForm(); setEmailOpen(true); }}
              className="w-full flex items-center justify-between"
            >
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">Change Email</p>
                <p className="text-xs text-muted-foreground">Update your email address</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            <Separator />
            <button
              onClick={() => { resetPasswordForm(); setPasswordOpen(true); }}
              className="w-full flex items-center justify-between"
            >
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">Change Password</p>
                <p className="text-xs text-muted-foreground">Update your account password</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            <Separator />
            <button
              onClick={() => { resetDeleteForm(); setDeleteOpen(true); }}
              className="w-full flex items-center justify-between"
            >
              <div className="text-left">
                <p className="text-sm font-medium text-destructive">Delete Account</p>
                <p className="text-xs text-muted-foreground">Permanently remove your account</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        {/* Responsible Gaming */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Responsible Gaming
            </CardTitle>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => { resetSuspendForm(); setSuspendOpen(true); }}
              className="w-full flex items-center justify-between"
            >
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">Take a Break</p>
                <p className="text-xs text-muted-foreground">Temporarily suspend your account</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        {/* Biometric Login */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Fingerprint className="w-4 h-4 text-primary" />
              Biometric Login
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Enable {biometricLabel}</p>
                <p className="text-xs text-muted-foreground">
                  {biometricAvailable
                    ? `Quick sign-in with ${biometricLabel}`
                    : 'Not available on this device'}
                </p>
              </div>
              <Switch
                checked={biometricEnabled}
                onCheckedChange={handleBiometricToggle}
                disabled={biometricLoading || !biometricAvailable}
              />
            </div>
          </CardContent>
        </Card>

        {/* Legal */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Legal
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 px-0">
            <button
              onClick={() => openLink(LEGAL_LINKS.terms)}
              className="w-full flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Terms & Conditions</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            <Separator />
            <button
              onClick={() => openLink(LEGAL_LINKS.privacy)}
              className="w-full flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Privacy Policy</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            <Separator />
            <button
              onClick={() => openLink(LEGAL_LINKS.cookies)}
              className="w-full flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Cookie className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Cookie Policy</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            <Separator />
            {/* SAB-706: per-category cookie toggles, reachable in Settings so native
                users retain control after the web consent banner is suppressed. */}
            <button
              onClick={() => setCookiePrefsOpen(true)}
              className="w-full flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Cookie className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Cookie preferences</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            <CustomizeModal open={cookiePrefsOpen} onClose={() => setCookiePrefsOpen(false)} />
            <Separator className="bg-border/50" />
            <button
              onClick={() => navigate('/responsible-gaming')}
              className="w-full flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Responsible Gaming</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

      </main>

      {/* Change Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={(open) => { setEmailOpen(open); if (!open) resetEmailForm(); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display">Change Email</DialogTitle>
          </DialogHeader>

          {emailSuccess ? (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
              <p className="text-sm text-foreground font-medium">
                Confirmation email sent to {newEmail.trim().toLowerCase()}.
              </p>
              <p className="text-xs text-muted-foreground">
                Click the link in that email to complete the change.
              </p>
            </div>
          ) : (
            <form onSubmit={handleChangeEmail} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-foreground">Current Email</Label>
                <p className="text-sm text-muted-foreground bg-muted/40 border border-border rounded-md px-3 py-2">
                  {user?.email}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-email" className="text-foreground">New Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="you@example.com"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  required
                  className="bg-input border-border text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-password" className="text-foreground">Password</Label>
                <div className="relative">
                  <Input
                    id="email-password"
                    type={showEmailPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={emailPassword}
                    onChange={e => setEmailPassword(e.target.value)}
                    required
                    className="bg-input border-border text-foreground pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEmailPassword(!showEmailPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showEmailPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {emailError && (
                <p className="text-sm text-destructive">{emailError}</p>
              )}

              <Button type="submit" className="w-full" disabled={emailSubmitting}>
                {emailSubmitting ? 'Sending...' : 'Update Email'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordOpen} onOpenChange={(open) => { setPasswordOpen(open); if (!open) resetPasswordForm(); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display">Change Password</DialogTitle>
          </DialogHeader>

          {passwordSuccess ? (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
              <p className="text-sm text-foreground font-medium">Password updated successfully!</p>
            </div>
          ) : (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password" className="text-foreground">Current Password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrent ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    required
                    className="bg-input border-border text-foreground pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-foreground">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNew ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="bg-input border-border text-foreground pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-foreground">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="bg-input border-border text-foreground pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {passwordError && (
                <p className="text-sm text-destructive">{passwordError}</p>
              )}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteOpen} onOpenChange={(open) => { setDeleteOpen(open); if (!open) resetDeleteForm(); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete Account
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-4">
              <p className="text-sm text-foreground leading-relaxed">
                This will permanently delete your account and all data. This cannot be undone.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="delete-confirm" className="text-foreground">
                Type <span className="font-bold text-destructive">DELETE</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                placeholder="DELETE"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                className="bg-input border-border text-foreground"
                autoComplete="off"
              />
            </div>

            {deleteError && (
              <p className="text-sm text-destructive">{deleteError}</p>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
              >
                {deleting ? 'Deleting...' : 'Delete My Account'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Biometric Password Dialog */}
      <Dialog open={biometricPasswordOpen} onOpenChange={(open) => { setBiometricPasswordOpen(open); if (!open) resetBioPasswordForm(); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display flex items-center gap-2">
              <Fingerprint className="w-5 h-5 text-primary" />
              Enable {biometricLabel}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleBioPasswordSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter your password to enable {biometricLabel} sign-in. Your credentials will be stored securely on this device.
            </p>

            <div className="space-y-2">
              <Label htmlFor="bio-password" className="text-foreground">Password</Label>
              <div className="relative">
                <Input
                  id="bio-password"
                  type={showBioPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={bioPassword}
                  onChange={e => setBioPassword(e.target.value)}
                  required
                  className="bg-input border-border text-foreground pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowBioPassword(!showBioPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showBioPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {bioPasswordError && (
              <p className="text-sm text-destructive">{bioPasswordError}</p>
            )}

            <Button type="submit" className="w-full" disabled={bioPasswordSubmitting}>
              {bioPasswordSubmitting ? 'Verifying...' : `Enable ${biometricLabel}`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Suspend Account Dialog */}
      <Dialog open={suspendOpen} onOpenChange={(open) => { setSuspendOpen(open); if (!open) resetSuspendForm(); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-display flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Take a Break
            </DialogTitle>
          </DialogHeader>

          {suspendSuccess ? (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
              <p className="text-sm text-foreground font-medium">Account suspended successfully.</p>
              <p className="text-xs text-muted-foreground">You will be signed out momentarily.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Suspend your account to take a break from Sabotage Fantasy. You won't be able to log in during this period.
              </p>

              <div className="space-y-2">
                <Label className="text-foreground">Duration</Label>
                <div className="grid grid-cols-3 gap-2">
                  {([['1week', '1 Week'], ['2weeks', '2 Weeks'], ['1month', '1 Month']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setSuspendDuration(key)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        suspendDuration === key
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-muted/40 text-foreground hover:border-primary/30'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => setSuspendDuration('custom')}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    suspendDuration === 'custom'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-muted/40 text-foreground hover:border-primary/30'
                  }`}
                >
                  Custom Duration (30+ days)
                </button>
                {suspendDuration === 'custom' && (
                  <Input
                    type="date"
                    value={customDate}
                    onChange={e => setCustomDate(e.target.value)}
                    min={getMinCustomDate()}
                    className="bg-input border-border text-foreground"
                  />
                )}
              </div>

              {suspendError && (
                <p className="text-sm text-destructive">{suspendError}</p>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSuspendOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSuspendAccount}
                  disabled={suspending || !suspendDuration}
                >
                  {suspending ? 'Suspending...' : 'Suspend Account'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppSettings;